import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, Button, Row, Col } from "antd";
import { useFetchApi } from "../../hooks/useFetchApi";
import ImageUploader from "../../components/ImageUpload";
import { format } from "date-fns";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Option } = Select;

const AddNewModal: React.FC<{
  heading?: string;
  status: boolean;
  onClose: any;
  onSuccess?: any;
  data?: any;
  stockMarkets?: any;
  type: string;
  sectors: Array<any>;
  quarters: Array<any>;
}> = ({
  status,
  onClose,
  onSuccess,
  data = {},
  stockMarkets = [],
  heading,
  type,
  sectors,
  quarters,
}): React.ReactElement => {
  const [d, setD] = useState<any>(data || {});
  const { execute } = useFetchApi(
    "/api/manage-stocks",
    {},
    data && Object.keys(data).length ? "PUT" : "POST",
    onSuccess,
  );

  const handleChange = (id: string, value: any) => {
    setD((prev: any) => ({ ...prev, [id]: value }));
  };

  const handleOnSubmit = () => {
    const formData = new FormData();

    if (data.id) formData.append("id", data.id);

    for (const key in d) {
      if (d.hasOwnProperty(key) && formData.get(key) === null) {
        formData.append(key, d[key]);
      }
    }

    execute(formData, { "Content-Type": "multipart/form-data" });
    onClose({ target: { id: type } });
  };

  const handleClose = () => {
    onClose({ target: { id: type } });
  };

  useEffect(() => {
    if (
      data &&
      Object.keys(data).length &&
      sectors.length &&
      quarters.length &&
      stockMarkets.length
    ) {
      setD({
        stock_market_id: parseInt(stockMarkets[0].id),
        company_quarter_id: quarters[0].id,
        sector_id: sectors[0].id,
        whitelisted: 0,
        ...data,
      });
    }

    return () => setD({});
  }, [data, sectors, quarters, stockMarkets]);

  return (
    <Modal
      open={status}
      title={heading}
      onCancel={handleClose}
      width={900}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button type="primary" onClick={handleOnSubmit}>
            Submit
          </Button>
          <Button onClick={handleClose}>Close</Button>
        </div>
      }
    >
      <div className="overflow-auto max-h-[70vh] py-4 px-1">
        <Form layout="vertical">
          {/* Dropdowns Row */}
          <Row gutter={16}>
            {stockMarkets?.length > 0 && (
              <Col span={8}>
                <Form.Item label="Stock Markets">
                  <Select
                    value={d.stock_market_id}
                    onChange={(val) => handleChange("stock_market_id", val)}
                  >
                    {stockMarkets.map((market: any) => (
                      <Option key={market.id} value={parseInt(market.id)}>
                        {market.market_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {sectors?.length > 0 && (
              <Col span={8}>
                <Form.Item label="Sectors">
                  <Select
                    value={d.sector_id}
                    onChange={(val) => handleChange("sector_id", val)}
                  >
                    {sectors.map((sector: any) => (
                      <Option key={sector.id} value={sector.id}>
                        {sector.sector_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

            {quarters?.length > 0 && (
              <Col span={8}>
                <Form.Item label="Company Quarters">
                  <Select
                    value={d.company_quarter_id}
                    onChange={(val) => handleChange("company_quarter_id", val)}
                  >
                    {quarters.map((quarter: any) => (
                      <Option key={quarter.id} value={quarter.id}>
                        {quarter.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          {/* Company Info Row */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Company Name">
                <Input
                  value={d.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ticker Symbol (e.g. AAPL)">
                <Input
                  value={d.ticker_symbol}
                  onChange={(e) =>
                    handleChange("ticker_symbol", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Company Start Date">
                <DatePicker
                  className="w-full"
                  value={
                    d.company_start_date && d.company_start_date !== "null"
                      ? dayjs(d.company_start_date)
                      : null
                  }
                  onChange={(_, dateStr) =>
                    handleChange("company_start_date", dateStr)
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          {/* OHLC Row */}
          <Row gutter={16}>
            {["open", "high", "low", "closed"].map((field) => (
              <Col span={6} key={field}>
                <Form.Item
                  label={field.charAt(0).toUpperCase() + field.slice(1)}
                >
                  <Input
                    value={d[field]}
                    onChange={(e) => handleChange(field, e.target.value)}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>

          {/* Change & Volume Row */}
          <Row gutter={16}>
            {[
              { id: "change_in_amount", label: "Change in Amount" },
              { id: "change_perc", label: "Change in %" },
              { id: "total_trades", label: "Total Trades" },
              { id: "volume", label: "Volume" },
            ].map(({ id, label }) => (
              <Col span={6} key={id}>
                <Form.Item label={label}>
                  <Input
                    value={d[id]}
                    onChange={(e) => handleChange(id, e.target.value)}
                  />
                </Form.Item>
              </Col>
            ))}
          </Row>

          {/* Misc Row */}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label="Turnover">
                <Input
                  value={d.turnover}
                  onChange={(e) => handleChange("turnover", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Last Trading Date">
                <Input
                  value={d.last_traded_date}
                  onChange={(e) =>
                    handleChange("last_traded_date", e.target.value)
                  }
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Status">
                <Input
                  value={d.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Is Whitelisted?">
                <Select
                  value={d.whitelisted}
                  onChange={(val) => handleChange("whitelisted", val)}
                >
                  <Option value={1}>Yes</Option>
                  <Option value={0}>No</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Image & Textareas */}
          <Form.Item>
            <ImageUploader
              onUpload={(file: any) => handleChange("company_logo", file)}
              file={d.company_logo}
              title="Company Logo"
            />
          </Form.Item>

          {[
            { id: "company_profile", label: "Company Profile Description" },
            {
              id: "income_statement_methodology",
              label: "Income Statement Methodology",
            },
            {
              id: "balance_sheet_methodology",
              label: "Balance Sheet Methodology",
            },
            { id: "cash_flow_methodology", label: "Cash Flow Methodology" },
            {
              id: "ai_reviewed_summary",
              label:
                "AI Reviewed Summary (This will get displayed for the Detailed Analysis)",
            },
          ].map(({ id, label }) => (
            <Form.Item label={label} key={id}>
              <TextArea
                rows={5}
                value={d[id] && d[id] !== "null" ? d[id] : ""}
                onChange={(e) => handleChange(id, e.target.value)}
              />
              {id === "ai_reviewed_summary" && d.ai_summary_updated_at && (
                <p className="text-xs text-gray-400 mt-1">
                  Last AI update:{" "}
                  {new Date(d.ai_summary_updated_at).toLocaleString()}
                </p>
              )}
            </Form.Item>
          ))}
        </Form>
      </div>
    </Modal>
  );
};

export default AddNewModal;
